import React from 'react';

import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { Group } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectFacecoverVersion } from '@vlapps/point/src/app/store/selectors';

import { AppliedFiltersBadge, EventTypeVisibilityButton } from './components';
import {
  FacecoverScoreFilterWithRightPopover,
  GeolocationFilterWithRightPopover,
  VideoAnalyticsScoreFilterWithRightPopover,
  VideoAnalyticsStreamsFilterWithRightPopover,
} from './side-menu';
import { useWorkspaceFilters } from './useWorkspaceFilters';
import type { VideoAnalyticsFilterConfig } from './videoAnalyticsFilters';
import { getVideoAnalyticsFilterActivity } from './workspaceFilterActivity';
import { useWorkspaceTranslation } from '../i18n';
import st from './WorkspaceFiltersPanel.module.sass';

interface VideoAnalyticsFiltersPanelProps {
  data?: VideoAnalyticsFilterConfig;
  onBackClick: VoidFunction;
}

function VideoAnalyticsFiltersPanel({
  onBackClick,
  data = undefined,
}: VideoAnalyticsFiltersPanelProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { t: tWorkspace } = useWorkspaceTranslation();
  const { filters, updateFilters } = useWorkspaceFilters();
  const facecoverVersion = useSelector(selectFacecoverVersion);

  if (!data) return null;

  const isFacecoverV2 = data?.analyticType === 'facecover' && facecoverVersion === 'v2';
  const facecoverSubtype = filters.videoAnalytics.facecover.filters.subtype;
  const activeFacecoverField = data.scoreFields?.find(({ key }) => key === facecoverSubtype);
  const activity = getVideoAnalyticsFilterActivity(filters, {
    analyticType: data.analyticType,
    facecoverVersion,
    scoreField: data.scoreField,
    scoreFields: data.scoreFields,
  });
  const analytic = filters.videoAnalytics[data.analyticType];
  const isEnabled = analytic?.isEnabled === true;
  const getBadge = (isApplied: boolean) => (
    <AppliedFiltersBadge
      isApplied={isApplied}
      isEnabled={isEnabled}
    />
  );

  return (
    <Group
      gapSize="0"
      vertical
    >
      <FiltersHeader
        customTitle={t(data.titleKey)}
        mode="goBack"
        onClick={onBackClick}
        rightIcon={(
          <EventTypeVisibilityButton
            isEnabled={isEnabled}
            onChange={(nextIsEnabled) => updateFilters({
              videoAnalytics: {
                ...filters.videoAnalytics,
                [data.analyticType]: {
                  ...analytic,
                  isEnabled: nextIsEnabled,
                },
              },
            })}
            testId={`videoAnalytics.${data.analyticType}.visibility`}
          />
        )}
      />

      <VideoAnalyticsStreamsFilterWithRightPopover
        badge={getBadge(activity.streams)}
        popoverProps={{
          analyticType: data.analyticType,
        }}
        testId="streamIdsFilter"
        value={t('video stream')}
      />
      <div className={st.Divider} />

      {!!isFacecoverV2 && (
        <React.Fragment>
          <FacecoverScoreFilterWithRightPopover
            badge={getBadge(activity.score)}
            popoverProps={{
              scoreFields: data.scoreFields,
            }}
            testId="facecoverScoreFilter"
            value={t(activeFacecoverField?.labelKey ?? 'videoAnalytics:filters.score.valueLabel')}
          />
          <div className={st.Divider} />
        </React.Fragment>
      )}

      {!isFacecoverV2 && !!data?.scoreField && (
        <React.Fragment>
          <VideoAnalyticsScoreFilterWithRightPopover
            badge={getBadge(activity.score)}
            popoverProps={{
              analyticType: data.analyticType,
              scoreField: data.scoreField,
            }}
            testId={`popover.${data.scoreField.id}`}
            value={t(data.scoreField.labelKey)}
          />
          <div className={st.Divider} />
        </React.Fragment>
      )}

      <GeolocationFilterWithRightPopover
        badge={getBadge(activity.location)}
        popoverProps={{
          analyticType: data.analyticType,
          idPrefix: `videoAnalytics.${data.analyticType}`,
        }}
        testId="geolocationFilter"
        value={tWorkspace('geolocation_filter.title')}
      />
      <div className={st.Divider} />
    </Group>
  );
}

export default VideoAnalyticsFiltersPanel;
